// src/components/SurveyForm/Questions/RangeQuestion/RangeQuestion.jsx

import React, { Fragment } from 'react';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Box,
  Divider,
  FormLabel,
  IconButton,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

/**
 * Componente para preguntas de rango con estrellas
 * Permite seleccionar un valor entre 1 y el score máximo
 */
const RangeQuestion = ({
  questionName,
  description,
  score = 5,
  currentValue = 0,
  index,
  questionId,
  hasError = false,
  primaryColor = '#03aae4',
  questionRef,
  onValueChange,
  hasOwnBold,
}) => {
  /**
   * Maneja el cambio de valor al hacer clic en una estrella
   * @param {number} newValue - Nuevo valor seleccionado (1-based)
   */
  const handleStarClick = (newValue) => {
    onValueChange(newValue);
  };

  /**
   * Renderiza una estrella individual
   * @param {number} value - Valor de la estrella (0-based)
   * @param {number} starIndex - Índice para identificar la primera estrella
   */
  const renderStar = (value, starIndex) => {
    const starValue = value + 1; // Convertir a 1-based
    const isFilled = currentValue >= starValue;

    return (
      <Box key={value}>
        <IconButton
          ref={starIndex === 0 ? questionRef : null}
          color="primary"
          component="label"
          onClick={() => handleStarClick(starValue)}
          style={{
            color: hasError ? 'red' : primaryColor,
          }}
          aria-label={`Seleccionar ${starValue} de ${score} estrellas`}
        >
          {isFilled ? <StarIcon /> : <StarBorderIcon />}
        </IconButton>
      </Box>
    );
  };

  return (
    <Fragment>
      {/* Título de la pregunta */}
      <FormLabel
        style={{
          fontSize: '1.1em',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '0.8em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {/* Descripción opcional */}
      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      {/* Contenedor de estrellas */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '1.1em 0',
          overflow: 'auto',
        }}
      >
        {[...Array(score).keys()].map((value, starIndex) =>
          renderStar(value, starIndex)
        )}
      </Box>

      {/* Contador de estrellas seleccionadas */}
      <Typography
        variant="caption"
        display="block"
        gutterBottom
        style={{
          textAlign: 'center',
          color: hasError ? 'red' : 'inherit',
          fontWeight: hasError ? 'bold' : 'normal',
        }}
      >
        {Number(currentValue)} / {score}
      </Typography>

      {/* Mensaje de error si no está respondida */}
      {hasError && (
        <Typography
          variant="caption"
          display="block"
          style={{
            textAlign: 'center',
            color: 'red',
            marginTop: '0.5em',
            fontWeight: 'bold',
          }}
        >
          Este campo es requerido
        </Typography>
      )}

      <Divider variant="middle" />
    </Fragment>
  );
};

RangeQuestion.propTypes = {
  questionName: PropTypes.string.isRequired,
  description: PropTypes.string,
  score: PropTypes.number,
  currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  index: PropTypes.number.isRequired,
  questionId: PropTypes.number.isRequired,
  hasError: PropTypes.bool,
  primaryColor: PropTypes.string,
  questionRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onValueChange: PropTypes.func.isRequired,
};

export default RangeQuestion;